/**
 * Constrains border-radius values to prevent visual artifacts when adjacent corners overlap.
 *
 * CSS border-radius can cause "tails" or incorrect clipping when:
 * - Sum of adjacent horizontal radii exceeds total width
 * - Sum of adjacent vertical radii exceeds total height
 *
 * This function proportionally scales down overlapping radii while preserving
 * the visual relationship between corners.
 */

const getConstrainedRadii = (radii, width, height) => {
  let {
    topLeft: { horizontal: tlH, vertical: tlV },
    topRight: { horizontal: trH, vertical: trV },
    bottomRight: { horizontal: brH, vertical: brV },
    bottomLeft: { horizontal: blH, vertical: blV },
  } = radii;

  // Prevent horizontal overlap: adjacent corners on the same edge
  // Top edge: topLeft + topRight must not exceed total width
  const topHorizontalSum = tlH + trH;
  const bottomHorizontalSum = blH + brH;

  if (topHorizontalSum > width) {
    // Scale both corners proportionally to fit within width
    const scale = width / topHorizontalSum;
    tlH *= scale;
    trH *= scale;
  }

  if (bottomHorizontalSum > width) {
    // Scale both corners proportionally to fit within width
    const scale = width / bottomHorizontalSum;
    blH *= scale;
    brH *= scale;
  }

  // Prevent vertical overlap: adjacent corners on the same side
  // Left side: topLeft + bottomLeft must not exceed total height
  const leftVerticalSum = tlV + blV;
  const rightVerticalSum = trV + brV;

  if (leftVerticalSum > height) {
    // Scale both corners proportionally to fit within height
    const scale = height / leftVerticalSum;
    tlV *= scale;
    blV *= scale;
  }

  if (rightVerticalSum > height) {
    // Scale both corners proportionally to fit within height
    const scale = height / rightVerticalSum;
    trV *= scale;
    brV *= scale;
  }

  return {
    topLeft: { horizontal: tlH, vertical: tlV },
    topRight: { horizontal: trH, vertical: trV },
    bottomRight: { horizontal: brH, vertical: brV },
    bottomLeft: { horizontal: blH, vertical: blV },
  };
};

export default getConstrainedRadii;
